import { useId, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PROJECT_COLORS, PROJECT_ICONS, projectColorInk } from "@/lib/projectIcons";
import { ProjectIcon } from "./ProjectIcon";

type Appearance = { icon: string | null; color: string };
export function ProjectIconPicker({ value, projectKey, onChange, disabled }: {
  value: Appearance; projectKey?: string; onChange: (value: Appearance) => void; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const search = useRef<HTMLInputElement>(null);
  return <Popover.Root open={open} onOpenChange={setOpen}>
    <Popover.Trigger asChild><button type="button" disabled={disabled} aria-label={t("projectIcons.choose")} title={t("projectIcons.choose")}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border-strong bg-bg transition-colors hover:border-accent data-[state=open]:border-accent disabled:cursor-not-allowed disabled:opacity-50">
      <ProjectIcon project={{ ...value, key: projectKey }} size={36} tile />
    </button></Popover.Trigger>
    <Popover.Portal><Popover.Content align="start" sideOffset={8} collisionPadding={16} aria-label={t("projectIcons.choose")}
      className="z-[60] flex max-h-[var(--radix-popover-content-available-height)] w-[392px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-xl border border-border-strong bg-elevated shadow-2xl"
      onOpenAutoFocus={event => { event.preventDefault(); search.current?.focus(); }}
      onKeyDown={event => event.stopPropagation()}>
      <PickerContent value={value} projectKey={projectKey} onChange={onChange} searchRef={search} onSelect={() => setOpen(false)} />
    </Popover.Content></Popover.Portal>
  </Popover.Root>;
}

function PickerContent({ value, projectKey, onChange, onSelect, searchRef }: {
  value: Appearance; projectKey?: string; onChange: (value: Appearance) => void; onSelect: () => void; searchRef: RefObject<HTMLInputElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const korean = i18n.language.startsWith("ko");
  const [query, setQuery] = useState("");
  const options = PROJECT_ICONS.filter(option => `${option.en} ${option.ko}`.toLowerCase().includes(query.trim().toLowerCase()));
  const selectedIndex = Math.max(0, options.findIndex(option => option.value === value.icon));
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-symbol]")];
    const current = buttons.indexOf(event.target as HTMLButtonElement);
    if (current < 0) return;
    const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -10, ArrowDown: 10 }[event.key];
    const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : offset !== undefined ? Math.max(0, Math.min(buttons.length - 1, current + offset)) : null;
    if (next !== null) { event.preventDefault(); buttons[next]?.focus(); }
  };
  return <>
    <div role="group" aria-label={t("projectIcons.colors")} className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3.5">
      {PROJECT_COLORS.map(color => <button key={color.value} type="button" aria-label={korean ? color.ko : color.en} title={korean ? color.ko : color.en} aria-pressed={value.color.toLowerCase() === color.value}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-black/10 transition-transform hover:scale-110"
        style={{ background: color.value, color: projectColorInk(color.value) }} onClick={() => onChange({ ...value, color: color.value })}>
        {value.color.toLowerCase() === color.value && <Check size={15} strokeWidth={2.5} />}
      </button>)}
    </div>
    <div className="flex shrink-0 items-center gap-2 border-b border-border px-4">
      <Search size={15} className="shrink-0 text-faint" />
      <input ref={searchRef} type="search" value={query} aria-label={t("projectIcons.searchIcons")}
        placeholder={t("projectIcons.searchIcons")} className="h-10 min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none placeholder:text-faint"
        onChange={event => setQuery(event.target.value)} onKeyDown={event => {
          if (event.key === "Enter") event.preventDefault();
          if (event.key === "ArrowDown" && !event.nativeEvent.isComposing) {
            event.preventDefault(); document.getElementById(id)?.querySelector<HTMLButtonElement>("button[tabindex='0']")?.focus();
          }
        }} />
    </div>
    <div id={id} role="group" aria-label={t("projectIcons.icons")} className="max-h-[252px] min-h-0 overflow-y-auto p-3" onKeyDown={move}>
      {options.length ? <div className="grid grid-cols-10 gap-1">{options.map((symbol, index) => {
        const label = korean ? symbol.ko : symbol.en;
        return <button type="button" key={symbol.value} data-symbol aria-label={label} title={label} aria-pressed={symbol.value === value.icon} tabIndex={index === selectedIndex ? 0 : -1}
          className={`flex aspect-square items-center justify-center rounded-md border transition-colors hover:bg-hover ${symbol.value === value.icon ? "border-accent bg-active" : "border-transparent"}`}
          onClick={() => { onChange({ ...value, icon: symbol.value }); onSelect(); }}>
          <ProjectIcon project={{ icon: symbol.value, color: value.color, key: projectKey }} size={20} />
        </button>;
      })}</div> : <p className="py-8 text-center text-[13px] text-muted">{t("common.noResults")}</p>}
    </div>
  </>;
}
